import React from 'react';
import { action, observable, computed, makeObservable, reaction, comparer } from 'mobx';
import { ApiAccountDefinition, ApiAccount } from '@api';
import { FormInstance } from '@ui-library/form';
import { DestinationAccount } from '@stores/destination/types';

import type { AccountResourceAdapter } from './resourceAdapter';

/**
 * The kinds of resource an account can be managed for. `dataRetention` reuses the
 * generic AMF lifecycle for a workspace's own event-backup storage accounts.
 */
export type AccountResourceType = 'destination' | 'source' | 'dataRetention';

export class AccountManagementStore {
  private reactionDisposer: (() => void) | null = null;

  public form: FormInstance;

  public supportedAccountTypes: string[] = [];

  public resourceType: AccountResourceType;

  public resourceDefinitionName: string;

  /**
   * This resource type's departures from the framework's default behaviour, supplied
   * by the host that mounts it. Absent for source and destination accounts.
   *
   * Readonly and set once in the constructor: consumers pick hooks and components out
   * of it during render, so it has to be fixed for the store's lifetime (see
   * `useNoConnectionTest`). Deliberately not `@observable` — it is a module-level
   * constant, never reassigned, and nothing should re-render on it.
   */
  public readonly adapter?: AccountResourceAdapter;

  @observable private _legacyAccounts: DestinationAccount[] = [];

  @observable private accountId: string;

  @observable private accountDefinitionName: string;

  @observable private accounts: ApiAccount[];

  @observable private accountDefinitions: ApiAccountDefinition[];

  @observable private deleteAccountId: string;

  @observable private editAccountId: string;

  // Cross-step selection for the data-retention two-step create flow: the chosen
  // provider (storage type, e.g. 'S3') before a concrete account definition
  // (auth type) is resolved. Unused by source/destination flows.
  @observable private providerType: string;

  constructor(
    form: FormInstance,
    config: {
      resourceType: AccountResourceType;
      resourceDefinitionName: string;
      supportedAccountTypes?: string[];
      adapter?: AccountResourceAdapter;
    },
  ) {
    makeObservable(this);

    this.form = form;
    this.accountId = '';
    this.accountDefinitionName = '';
    this.accountDefinitions = [];
    this.accounts = [];
    this.deleteAccountId = '';
    this.editAccountId = '';
    this.providerType = '';
    this.supportedAccountTypes = config.supportedAccountTypes || [];
    this.resourceType = config.resourceType;
    this.resourceDefinitionName = config.resourceDefinitionName;
    this.adapter = config.adapter;
  }

  @action.bound
  public setLegacyAccounts(accounts: DestinationAccount[]) {
    // Third setter driven by the same loader effect as `setAccounts` /
    // `setAccountDefinitions`, with the same reference churn on every SWR settle —
    // so it gets the same no-op guard. `legacyAccounts` currently has narrower
    // consumers than `accounts` (a length check and a role check in
    // accountManagement/index.tsx), so this is consistency rather than a second
    // observed bug: leaving one of the three unguarded would mean a revalidation
    // still re-renders that subtree for no reason.
    if (comparer.structural(this._legacyAccounts, accounts)) {
      return;
    }

    this._legacyAccounts = accounts;
  }

  @computed
  public get legacyAccounts() {
    return this._legacyAccounts;
  }

  @action.bound
  public setForm(form: FormInstance) {
    this.form = form;
  }

  @action.bound
  public setReactionDisposer(onComplete: (accountId: string) => void) {
    this.reactionDisposer = reaction(
      () => this.accountId,
      (accountId) => {
        if (accountId) {
          onComplete(accountId);
        }
      },
    );
  }

  @action.bound
  public getPayload() {
    const formData = this.form.getFieldsValue();

    return {
      name: formData.name,
      options: formData.options || {},
      accountDefinitionName: this.accountDefinitionName,
    };
  }

  @action.bound
  public setAccounts(accounts: ApiAccount[]) {
    // SWR revalidation (e.g. `revalidateOnFocus`) hands back a fresh array
    // reference with identical content on every settle. Replacing the observable
    // regardless changes `compatibleAccounts`' reference and re-renders every
    // consumer — including an open account picker or delete-confirmation modal,
    // dismissing it and dropping in-progress input. Only replace when the content
    // actually changed so no-op revalidations don't disrupt an open flow.
    // `comparer.structural` is MobX's own deep-equal — observable-aware, so it compares
    // the observable directly against the plain array with no `toJS` clone or lodash.
    if (comparer.structural(this.accounts, accounts)) {
      return;
    }
    this.accounts = accounts;
  }

  @computed
  public get compatibleAccounts() {
    return this.accounts;
  }

  public get compatibleAccountDefinitions() {
    return this.accountDefinitions.filter((accountDefinition) =>
      this.supportedAccountTypes.includes(accountDefinition.name),
    );
  }

  /**
   * A pure read, and deliberately *not* an action: MobX runs actions untracked, so
   * annotating this one made every render-time read resolve an account without
   * subscribing to the list it came from. That is invisible in isolation and fatal in
   * sequence — the accounts are seeded from an effect, one commit after the render
   * that first reports ready, so the first render resolves against an empty list and
   * nothing re-renders when the real one lands (the storage card's permanently
   * nameless Connected badge). Reads through here now register the dependency.
   *
   * An arrow property rather than a prototype method because call sites destructure it
   * off the store, which would strip `this` from a plain method — `@action.bound` was
   * carrying the binding, not any mutation.
   */
  public findAccountById = (accountId: string): ApiAccount | undefined =>
    this.accounts.find((account) => account.id === accountId);

  @action.bound
  public setAccountId(value: string) {
    this.accountId = value;
  }

  @computed
  public get selectedAccountId(): string {
    return this.accountId;
  }

  @action.bound
  public setAccountDefinitionName(value: string) {
    this.accountDefinitionName = value;
  }

  @computed
  public get selectedAccountDefinition() {
    return this.accountDefinitions.find(
      (accountDef) => accountDef.name === this.accountDefinitionName,
    );
  }

  @action.bound
  public setProviderType(value: string) {
    this.providerType = value;
  }

  @computed
  public get selectedProviderType() {
    return this.providerType;
  }

  /**
   * Whether accounts for this resource require a connectivity ("Test Connection")
   * check before persisting — true exactly when the resource supplies the slot that
   * satisfies it, so the gate can never be on without an affordance to clear it.
   *
   * A plain getter rather than a `@computed`: it reads the adapter, which is fixed
   * for the store's lifetime and holds no observable state, so there is nothing here
   * for MobX to invalidate.
   */
  public get requiresConnectionTest() {
    return !!this.adapter?.connectionTest;
  }

  /**
   * Option values implied by the edited account's definition rather than entered in
   * its form, and therefore absent from `form.getFieldsValue()` — which returns only
   * the paths of *registered* fields, so a value merely seeded via `setFieldsValue`
   * for a field the form does not render would be dropped.
   *
   * Empty unless the resource supplies `getDefinitionImpliedOptions`; resource types
   * that do not (source, destination) have no such options and are untouched. Also
   * empty outside an edit (no `editAccountId`), which is why the create path — where
   * the form does render the toggle — is unaffected.
   */
  private get editDefinitionImpliedOptions(): Record<string, unknown> {
    const getImpliedOptions = this.adapter?.getDefinitionImpliedOptions;

    if (!getImpliedOptions) {
      return {};
    }

    const account = this.findAccountById(this.editAccountId);

    return account ? getImpliedOptions(account.accountDefinitionName) : {};
  }

  /**
   * The raw credential config drawn from the form, merging the `options` and
   * `secret` namespaces, for a stateless connectivity validation call.
   *
   * The definition-implied options are folded in so the config that gets tested is
   * the same one an edit persists. Without them an IAM-role account's test would be
   * validated as if it used access keys — failing against creds that are in fact
   * fine, and gating Save on a verdict about a config nobody is saving.
   */
  @action.bound
  public getDrawnConfig(): Record<string, unknown> {
    const formData = this.form.getFieldsValue();

    return {
      ...(formData.options || {}),
      ...(formData.secret || {}),
      ...this.editDefinitionImpliedOptions,
    };
  }

  @action.bound
  public setAccountDefinitions(accountDefinitions: ApiAccountDefinition[]) {
    // Definitions are reconciled to the resource's canonical forms here so every
    // downstream consumer (create/edit forms, picker option summary, edit prefill)
    // renders the same field set regardless of the backend `uiConfig`. Resource types
    // that supply no `reconcileDefinition` pass through untouched.
    const reconcile = this.adapter?.reconcileDefinition;

    const reconciled = reconcile
      ? accountDefinitions.map((definition) =>
          this.supportedAccountTypes.includes(definition.name) ? reconcile(definition) : definition,
        )
      : accountDefinitions;

    // Same no-op guard as `setAccounts`, and for the same reason — this setter is called
    // from the same loader effect, on every run, with a list that is usually identical.
    // Reassigning mints fresh observable proxies that every consumer reading
    // `compatibleAccountDefinitions` (pickers, create/edit forms) sees as new objects,
    // re-rendering an open flow even though nothing about the definitions changed.
    // Compared *after* reconciliation so the comparison is against what would actually
    // be stored, not the raw input.
    if (comparer.structural(this.accountDefinitions, reconciled)) {
      return;
    }

    this.accountDefinitions = reconciled;
  }

  private getAccountDefinitionByAccountId(accountId: string): ApiAccountDefinition | undefined {
    const account = this.findAccountById(accountId);
    if (!account) {
      return undefined;
    }
    return this.accountDefinitions.find(
      (accountDef) => accountDef.name === account.accountDefinitionName,
    );
  }

  @action.bound
  public isAccountOAuth(accountId: string) {
    return this.getAccountDefinitionByAccountId(accountId)?.uiConfig.isOAuth || false;
  }

  @action.bound
  public isAccountDefinitionDeprecated(accountId: string) {
    return this.getAccountDefinitionByAccountId(accountId)?.displayOptions?.deprecated || false;
  }

  // Deprecation reason shown when an account can't be selected/created because its
  // definition is deprecated; undefined when the account definition isn't deprecated.
  @action.bound
  public getAccountDeprecationReason(accountId: string): string | undefined {
    const accountDefinition = this.getAccountDefinitionByAccountId(accountId);
    return accountDefinition?.displayOptions?.deprecated
      ? accountDefinition.displayOptions.deprecationLabel
      : undefined;
  }

  @action.bound
  public setDeleteAccountId(value: string) {
    this.deleteAccountId = value;
  }

  @computed
  public get selectedDeleteAccountId() {
    return this.deleteAccountId;
  }

  @action.bound
  public setEditAccountId(value: string) {
    this.editAccountId = value;
  }

  @computed
  public get selectedEditAccountId() {
    return this.editAccountId;
  }

  @action.bound
  public getEditNonOAuthPayload() {
    const formData = this.form.getFieldsValue();
    // Exclude secret fields that are empty: an empty value means the user did not
    // modify the secret, so we omit it to preserve the existing value on the backend.
    const filteredSecrets = Object.fromEntries(
      Object.entries(formData.secret || {}).filter(
        ([, v]) => v !== undefined && v !== '' && v !== null,
      ),
    );
    const options = {
      ...(formData.options || {}),
      ...filteredSecrets,
      // Last, so the definition — which an edit cannot change — always wins over a
      // form value. A PATCH replaces the whole `options` blob instead of merging per
      // key, so anything the form omits is erased from the stored account: these
      // must ride along or they are lost.
      ...this.editDefinitionImpliedOptions,
    };

    return {
      name: formData.name,
      options,
    };
  }

  /**
   * Clear every value under the `secret` form namespace. antd retains field values
   * across `Form.Item` unmounts (`preserve` defaults to true) and merges on
   * `setFieldsValue`, so a secret entered in one edit session would otherwise linger
   * in client state — revealable when the edit form re-opens in the same modal
   * session, or after a successful save. Resetting the current secret paths back to
   * their (empty) initial values guarantees secret inputs always start empty and no
   * previously-entered secret can persist in memory or be revealed.
   */
  @action.bound
  public resetSecretFields() {
    const { secret } = this.form.getFieldsValue() as { secret?: Record<string, unknown> };
    const secretPaths = Object.keys(secret ?? {}).map((key): (string | number)[] => [
      'secret',
      key,
    ]);
    if (secretPaths.length > 0) {
      this.form.resetFields(secretPaths);
    }
  }

  // Cleanup method
  public dispose() {
    if (this.reactionDisposer) {
      this.reactionDisposer();
      this.reactionDisposer = null;
    }
  }

  public reset() {
    this.accountId = '';
    this.accountDefinitionName = '';
    this.editAccountId = '';
    this.providerType = '';
    this.form.resetFields();
  }
}

export const AccountManagementCtx = React.createContext<AccountManagementStore | undefined>(
  undefined,
);

export const useAccountManagementStore = () => {
  const context = React.useContext(AccountManagementCtx);

  if (!context) {
    throw new Error('useAccountManagementStore must be used within AccountManagementCtx.Provider');
  }
  return context;
};
